import { Alert, Button } from "@breenova/ui";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { useAuth } from "@/auth/auth-context";
import { AuthLayout } from "@/routes/auth-layout";

/**
 * How often the screen asks whether the decision has been taken.
 *
 * Half a minute. Short enough that somebody sitting with the administrator sees it open
 * on its own — which is the moment this whole screen exists for — and long enough that a
 * tab left open overnight is not two thousand requests.
 */
const POLL_INTERVAL_MS = 30_000;

/**
 * Where a breeder waits between asking for an account and being given one.
 *
 * They are genuinely signed in: their password worked, their token is good, and there is
 * exactly one screen they may see. Refusing the sign-in instead would have been tidier to
 * implement and worse to be on the receiving end of — "your password is wrong" for a
 * password that is right teaches people to stop trusting the message.
 *
 * No chrome, for the same reason the forgotten-password confirmation has none: a
 * navigation bar leading nowhere invites clicking on it.
 */
export function AwaitingApprovalPage() {
  const { user, isApproved, refreshUser, refreshSession, signOut } = useAuth();
  const navigate = useNavigate();

  const [checking, setChecking] = useState(false);
  const admitted = useRef(false);

  const rejected = user?.approvalStatus === "Rejected";

  // Poll while waiting. A rejection is a decision, not a queue position, so nothing is
  // gained by asking again — the administrator would have to act for it to change, and
  // if they do, the next sign-in picks it up.
  useEffect(() => {
    if (rejected) {
      return;
    }

    const timer = setInterval(() => void refreshUser(), POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [refreshUser, rejected]);

  /*
   * Approved. Learning the decision is not the same as being able to act on it: the token
   * in hand still says this breeder is waiting, and every screen inside the app would
   * refuse them until it is exchanged. So swap it first, then go.
   *
   * The ref guards against React 19's development double-invoke and against the poll
   * landing again mid-exchange — a second refresh would present a token the first has
   * already rotated away, which the server correctly treats as theft and answers by
   * revoking the whole family.
   */
  useEffect(() => {
    if (!isApproved || admitted.current) {
      return;
    }

    admitted.current = true;

    void (async () => {
      await refreshSession();
      await navigate("/", { replace: true });
    })();
  }, [isApproved, refreshSession, navigate]);

  async function checkNow(): Promise<void> {
    setChecking(true);

    try {
      await refreshUser();
    } finally {
      setChecking(false);
    }
  }

  if (rejected) {
    return (
      <AuthLayout
        title="Not this time"
        subtitle="Your request for an account was not accepted."
      >
        <Alert tone="error">
          {user?.approvalNote?.trim() ||
            "No reason was given. If you think this is a mistake, speak to whoever runs this BreeNova and ask them to look again."}
        </Alert>

        <Button variant="secondary" className="self-start" onClick={() => void signOut()}>
          Sign out
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Almost there"
      subtitle="Your aviary is set up. Someone just has to let you in."
    >
      <Alert tone="warning">
        New accounts are approved by hand, so nobody but the breeders who should be here
        ends up with the run of the place. This page opens on its own the moment that
        happens — you can leave it up, or come back and sign in later.
      </Alert>

      <div className="flex flex-wrap items-center gap-sm">
        <Button onClick={() => void checkNow()} disabled={checking}>
          {checking ? "Checking…" : "Check again"}
        </Button>

        <Button variant="secondary" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>

      <p className="text-caption text-on-surface-variant">
        Signed in as {user?.email ?? "this account"}.
      </p>
    </AuthLayout>
  );
}
